/**
 * 标准动作集（M2-01 / DESIGN.md §7.4）。
 *
 * 这里注册**与文件系统无关**的编辑动作。文件动作（新建/打开/保存/导出）
 * 由 M2-03 的 registerFileActions 注册，「关于」由装配层注册（它要开 UI 面板）。
 *
 * 自由变换/复制图层/图层编组都依赖装配层注入的控制器（会话控制器/
 * `AppStore`）——快捷键与菜单位置在这里定死，未注入时（旧装配、单元测试
 * 用的最小夹具）enabled 恒为 false，菜单如实置灰，比一个点了没反应的
 * 菜单项诚实。真正注入之后（见 main.tsx）就是完整功能，不是占位。
 */
import { layerId, type LayerId } from '../../engine/tile/key';
import type { TileStore } from '../../engine/tile/store';
import type { GroupLayer, RasterLayer } from '../../document/types';
import { findLayer, layerDefaults } from '../../document/draft';
import { createAddLayerCommand } from '../commands/layer-commands';
import { addMaskToActiveLayer, deleteLayer, duplicateActiveLayer, groupActiveLayer } from '../layer-edit';
import type { AppStore } from '../store';
import { PHOTOSHOP_ACTION_SHORTCUTS } from './keymap';
import type { ActionRegistry } from './registry';

export interface StandardActionDeps {
  /** 新建图层要为图层申请 TileHandle。 */
  readonly tiles: TileStore;
  /** 图层 id 生成器。注入以便测试得到确定的 id。 */
  readonly nextLayerId: () => string;
  /** 自由变换入口（M2-14 的会话控制器）。未注入时动作置灰。 */
  readonly beginTransform?: (() => boolean) | undefined;
  /** 新建图层后设为活动（M3-01 语义：新建即活动由调用方负责）。 */
  readonly setActiveLayer?: ((id: LayerId) => void) | undefined;
  /**
   * 复制图层/图层编组要读当前选中图层（`store.activeLayerId`）并直接
   * `dispatch`——跟其余动作只经 `ActionServices` 不同，是因为这两个操作
   * 需要连带复制像素（TileStore），细节封在 `layer-edit.ts` 里，这里只是
   * 转发。未注入（旧装配/测试）时这两个动作恒置灰。
   */
  readonly store?: AppStore | undefined;
}

export function registerStandardActions(registry: ActionRegistry, deps: StandardActionDeps): void {
  /* ---------------- 编辑 ---------------- */

  registry.register({
    id: 'edit.undo',
    title: '还原',
    group: '编辑',
    section: 0,
    order: 0,
    shortcut: PHOTOSHOP_ACTION_SHORTCUTS['edit.undo'],
    enabled: (s) => s.canUndo,
    run: (services) => {
      services.undo();
    },
  });

  registry.register({
    id: 'edit.stepBackward',
    title: '后退一步',
    group: '编辑',
    section: 0,
    order: 1,
    shortcut: PHOTOSHOP_ACTION_SHORTCUTS['edit.stepBackward'],
    enabled: (s) => s.canUndo,
    run: (services) => {
      services.undo();
    },
  });

  registry.register({
    id: 'edit.stepForward',
    title: '前进一步',
    group: '编辑',
    section: 0,
    order: 2,
    shortcut: PHOTOSHOP_ACTION_SHORTCUTS['edit.stepForward'],
    enabled: (s) => s.canRedo,
    run: (services) => {
      services.redo();
    },
  });

  registry.register({
    id: 'edit.freeTransform',
    title: '自由变换',
    group: '编辑',
    section: 1,
    order: 0,
    shortcut: PHOTOSHOP_ACTION_SHORTCUTS['edit.freeTransform'],
    // 控制器未注入（旧装配/测试）时如实置灰，而不是点了没反应
    enabled: (s) => deps.beginTransform !== undefined && s.hasDocument,
    run: (services) => {
      if (deps.beginTransform?.() !== true) {
        services.hint('没有可变换的像素图层');
      }
    },
  });

  /* ---------------- 图层 ---------------- */

  registry.register({
    id: 'layer.new',
    title: '新建图层',
    group: '图层',
    section: 0,
    order: 0,
    shortcut: PHOTOSHOP_ACTION_SHORTCUTS['layer.new'],
    enabled: (s) => s.hasDocument,
    run: (services) => {
      const document = services.document();
      if (document === null) return;
      const id = layerId(deps.nextLayerId());
      const layer: RasterLayer = {
        ...layerDefaults(id, `图层 ${document.root.children.length + 1}`),
        kind: 'raster',
        tiles: deps.tiles.handle(id),
        bounds: { x: 0, y: 0, width: 0, height: 0 },
      };
      // 追加到根组末尾（图层面板的最上方按渲染序是最后合成的一层）
      services.dispatch(
        createAddLayerCommand({ layer, parentId: null, index: document.root.children.length }),
      );
      deps.setActiveLayer?.(id); // 新建即活动（PS 语义）
    },
  });

  registry.register({
    id: 'layer.newGroup',
    title: '新建组',
    group: '图层',
    section: 0,
    order: 1,
    // 真实 Photoshop 的"新建组"没有默认单键快捷键（跟"删除图层"同款理由）
    enabled: (s) => s.hasDocument,
    run: (services) => {
      const document = services.document();
      if (document === null) return;
      const id = layerId(deps.nextLayerId());
      const group: GroupLayer = {
        ...layerDefaults(id, `组 ${document.root.children.length + 1}`),
        kind: 'group',
        children: [],
        passThrough: true, // 真实 Photoshop 新建组默认就是穿透模式
        collapsed: false,
      };
      services.dispatch(
        createAddLayerCommand({ layer: group, parentId: null, index: document.root.children.length }),
      );
      deps.setActiveLayer?.(id);
    },
  });

  registry.register({
    id: 'layer.addMask',
    title: '添加图层蒙版',
    group: '图层',
    section: 0,
    order: 2,
    // 已经有蒙版的图层不能再加——跟真实 PS 蒙版按钮此时置灰一致
    enabled: (s) => {
      if (deps.store === undefined || !s.hasActiveLayer) return false;
      const document = deps.store.document;
      const id = deps.store.activeLayerId;
      if (document === null || id === null) return false;
      return findLayer(document.root, id)?.layer.mask === undefined;
    },
    run: (services) => {
      if (deps.store === undefined || !addMaskToActiveLayer(deps.store, deps)) {
        services.hint('没有选中的图层');
      }
    },
  });

  registry.register({
    id: 'layer.duplicate',
    title: '复制图层',
    group: '图层',
    section: 0,
    order: 3,
    shortcut: PHOTOSHOP_ACTION_SHORTCUTS['layer.duplicate'],
    // 控制器未注入（旧装配/测试）或没有选中图层时如实置灰
    enabled: (s) => deps.store !== undefined && s.hasActiveLayer,
    run: (services) => {
      if (deps.store === undefined) return;
      if (!duplicateActiveLayer(deps.store, deps)) services.hint('没有选中的图层');
    },
  });

  registry.register({
    id: 'layer.group',
    title: '图层编组',
    group: '图层',
    section: 1,
    order: 0,
    shortcut: PHOTOSHOP_ACTION_SHORTCUTS['layer.group'],
    enabled: (s) => deps.store !== undefined && s.hasActiveLayer,
    run: (services) => {
      if (deps.store === undefined) return;
      // 多选（图层面板 shift/cmd）时编那一组；否则退回单选当前活动图层
      const selected = deps.store.selectedLayerIds;
      const memberIds = selected.size > 1 ? [...selected] : undefined;
      if (!groupActiveLayer(deps.store, deps, memberIds)) {
        services.hint(memberIds !== undefined ? '所选图层不在同一个组里，无法编组' : '没有选中的图层');
      }
    },
  });

  registry.register({
    id: 'layer.delete',
    title: '删除图层',
    group: '图层',
    section: 0,
    order: 4,
    // 真实 Photoshop 删除图层没有默认单键快捷键（Delete/Backspace 删的是
    // 像素/选区内容，不是图层本身）——图层面板的垃圾桶图标或菜单才是入口，
    // 这里如实不注册快捷键，不要为了"有个键位"硬凑一个 PS 里不存在的绑定
    enabled: (s) => deps.store !== undefined && s.hasActiveLayer,
    run: (services) => {
      if (deps.store === undefined) return;
      const id = deps.store.activeLayerId;
      if (id === null || !deleteLayer(deps.store, id)) services.hint('没有选中的图层');
    },
  });

  // 选择菜单的动作在 selection-actions.ts（M2-07 起有真实实现）
}
